import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import PublicChrome from "./components/PublicChrome";
import RouteTracker from "./components/RouteTracker";
import RecoveryLinkRedirect from "./components/RecoveryLinkRedirect";
import LanguageSync from "./components/LanguageSync";
import { initializeConsent } from "@/lib/analytics";
import { AuthProvider } from "@/contexts/AuthProvider";
import RequireAdmin from "@/components/admin/RequireAdmin";
import RequireRole from "@/components/admin/RequireRole";
import AdminLayout from "@/layouts/AdminLayout";
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminContacts from "./pages/admin/Contacts";
import AdminEmail from "./pages/admin/Email";
import AdminSuppressions from "./pages/admin/Suppressions";
import AdminUsers from "./pages/admin/Users";
import AdminPosts from "./pages/admin/Posts";
import AdminPostEdit from "./pages/admin/PostEdit";
import AdminPostPreview from "./pages/admin/PostPreview";
import AdminForgotPassword from "./pages/admin/ForgotPassword";
import AdminResetPassword from "./pages/admin/ResetPassword";
import AdminNotAuthorized from "./pages/admin/NotAuthorized";
import Index from "./pages/Index";
import About from "./pages/About";
import Contact from "./pages/Contact";
import News from "./pages/News";
import NewsArticle from "./pages/NewsArticle";
import Services from "./pages/Services";
import AISkilling from "./pages/AISkilling";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import StudentStudyLogitech from "./pages/StudentStudyLogitech";
import NorrOmFramtiden from "./pages/NorrOmFramtiden";
import NotFound from "./pages/NotFound";
import Unsubscribe from "./pages/Unsubscribe";
import Newsletter from "./pages/Newsletter";
import NewsletterConfirm from "./pages/NewsletterConfirm";
import AdminSubscribers from "./pages/admin/Subscribers";

const queryClient = new QueryClient();

// The same set of routes is mounted under both `/` (English) and `/sv` (Swedish).
// LanguageSync reads the URL and switches i18next accordingly.
const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Index />} />
    <Route path="/about" element={<About />} />
    <Route path="/ai-skilling" element={<AISkilling />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/news" element={<News />} />
    <Route path="/services" element={<Services />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/student-study-logitech" element={<StudentStudyLogitech />} />
  </Routes>
);

const App = () => {
  // Initialize Google Consent Mode v2 on app load
  useEffect(() => {
    initializeConsent();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <LanguageSync />
            <RecoveryLinkRedirect />
            <ScrollToTop />
            <RouteTracker />
            <PublicChrome />
            <Routes>
            {/* Swedish mirror */}
            <Route path="/sv" element={<Index />} />
            <Route path="/sv/about" element={<About />} />
            <Route path="/sv/ai-skilling" element={<AISkilling />} />
            <Route path="/sv/contact" element={<Contact />} />
            <Route path="/sv/news" element={<News />} />
            <Route path="/sv/news/:slug" element={<NewsArticle />} />
            <Route path="/sv/services" element={<Services />} />
            <Route path="/sv/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/sv/student-study-logitech" element={<StudentStudyLogitech />} />
            <Route path="/sv/norr-om-framtiden" element={<NorrOmFramtiden />} />
            <Route path="/sv/newsletter" element={<Newsletter />} />

            {/* English (default) */}
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/ai-skilling" element={<AISkilling />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsArticle />} />
            <Route path="/services" element={<Services />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/student-study-logitech" element={<StudentStudyLogitech />} />
            <Route path="/norr-om-framtiden" element={<NorrOmFramtiden />} />
            <Route path="/newsletter" element={<Newsletter />} />

            {/* Email unsubscribe (shared EN/SV) */}
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/sv/unsubscribe" element={<Unsubscribe />} />

            {/* Newsletter confirmation */}
            <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
            <Route path="/sv/newsletter/confirm" element={<NewsletterConfirm />} />

            {/* Admin portal (invite-only, noindex) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/not-authorized" element={<AdminNotAuthorized />} />
            <Route
              path="/admin"
              element={
                <RequireAdmin>
                  <AdminLayout />
                </RequireAdmin>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route
                path="posts"
                element={
                  <RequireRole allowed={["admin"]}>
                    <AdminPosts />
                  </RequireRole>
                }
              />
              <Route
                path="posts/:id"
                element={
                  <RequireRole allowed={["admin"]}>
                    <AdminPostEdit />
                  </RequireRole>
                }
              />
              <Route
                path="posts/:id/preview"
                element={
                  <RequireRole allowed={["admin"]}>
                    <AdminPostPreview />
                  </RequireRole>
                }
              />
              <Route
                path="emails"
                element={
                  <RequireRole allowed={["admin"]}>
                    <AdminEmail />
                  </RequireRole>
                }
              />
              <Route
                path="emails/suppressions"
                element={
                  <RequireRole allowed={["admin"]}>
                    <AdminSuppressions />
                  </RequireRole>
                }
              />
              <Route
                path="users"
                element={
                  <RequireRole allowed={["admin"]}>
                    <AdminUsers />
                  </RequireRole>
                }
              />
              <Route
                path="subscribers"
                element={
                  <RequireRole allowed={["admin"]}>
                    <AdminSubscribers />
                  </RequireRole>
                }
              />
            </Route>

            {/* Old website URL redirects */}
            <Route path="/17/4/about-us" element={<Navigate to="/about" replace />} />
            <Route path="/17/4/about-us/" element={<Navigate to="/about" replace />} />
            <Route path="/17/5/news" element={<Navigate to="/news" replace />} />
            <Route path="/17/5/news/" element={<Navigate to="/news" replace />} />
            <Route path="/17/6/services" element={<Navigate to="/services" replace />} />
            <Route path="/17/6/services/" element={<Navigate to="/services" replace />} />
            <Route path="/17/7/our-team" element={<Navigate to="/about" replace />} />
            <Route path="/17/7/our-team/" element={<Navigate to="/about" replace />} />
            <Route path="/17/8/contact" element={<Navigate to="/contact" replace />} />
            <Route path="/17/8/contact/" element={<Navigate to="/contact" replace />} />
            <Route path="/17/8/contact//" element={<Navigate to="/contact" replace />} />

            {/* Additional legacy URL redirects (from Google Search Console) */}
            <Route path="/Kontakter" element={<Navigate to="/contact" replace />} />
            <Route path="/Kontakter/" element={<Navigate to="/contact" replace />} />
            <Route path="/17/1/home" element={<Navigate to="/" replace />} />
            <Route path="/17/1/home/" element={<Navigate to="/" replace />} />
            <Route path="/17/2/reports" element={<Navigate to="/news" replace />} />
            <Route path="/17/2/reports/" element={<Navigate to="/news" replace />} />
            <Route path="/Hybrida-arbetssätt-och-AI" element={<Navigate to="/news" replace />} />
            <Route path="/Hybrida-arbetssätt-och-AI/" element={<Navigate to="/news" replace />} />
            <Route path="/Student" element={<Navigate to="/ai-skilling" replace />} />
            <Route path="/Student/" element={<Navigate to="/ai-skilling" replace />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
  );
};

export default App;
